import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';

import type { GroupRow } from '../../services/api/types.gen';
import { groupColumns } from './groupColumns';

interface GroupsTableProps {
  rows: GroupRow[];
  onRowClick: (groupId: string) => void;
}

// No initial sort: the server returns editable groups first, then by name.
export const GroupsTable: React.FC<GroupsTableProps> = ({
  rows,
  onRowClick,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable<GroupRow>({
    data: rows,
    columns: groupColumns,
    getRowId: row => row.id,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className='overflow-auto rounded-md border border-border'>
      <Table className='min-w-[720px]'>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const label = flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                );
                const sorted = header.column.getIsSorted();
                return (
                  <TableHead key={header.id}>
                    {header.column.getCanSort() ? (
                      <button
                        type='button'
                        className='flex items-center font-medium hover:text-foreground'
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {label}
                        {sorted === 'asc' ? (
                          <ArrowUp className='ml-1 h-3 w-3' />
                        ) : sorted === 'desc' ? (
                          <ArrowDown className='ml-1 h-3 w-3' />
                        ) : (
                          <ChevronsUpDown className='ml-1 h-3 w-3 opacity-40' />
                        )}
                      </button>
                    ) : (
                      label
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map(row => (
            <TableRow
              key={row.id}
              data-state={row.original.archived_at ? 'archived' : undefined}
              className='cursor-pointer data-[state=archived]:opacity-60'
              onClick={() => onRowClick(row.original.id)}
            >
              {row.getVisibleCells().map(cell => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
